import { Button, Typography, Accordion } from '@cannect/new-components/atoms'
import cannectLogo from '@cannect/assets/svg/logo2.svg'
import biomaLogo from '@cannect/assets/svg/bioma.svg'
import SEO from 'components/SEO'
import useSignedUser from 'hooks/useSignedUser'
import { ArrowRight, Check, Minus, Dna, ShieldCheck, ArrowDown, User, Stethoscope } from 'lucide-react'
import { useState } from 'react'
import { useHistory } from 'react-router'
import {
  pillars,
  howItWorksSteps,
  comparisonTable,
  audienceCards,
  interpretationBlocks,
  pricingPlans,
  pricingLaunchLabel,
  faqPatientItems,
  faqProfessionalItems
} from './constants'

export const CannectCodeUI = () => {
  const { signedUser } = useSignedUser()
  const isProfessional = signedUser?.registration_type === 2
  const [activeFaqTab, setActiveFaqTab] = useState<'patient' | 'professional'>('patient')
  const history = useHistory()

  const handleCTA = () => {
    history.push('/produtos/1?limit=16&class_id=8&translated_name=')
  }

  return (
    <div className="w-full bg-gray-50">
      <SEO
        title="CANNECT Code — Teste Genético"
        description="Exame genético de suporte à decisão clínica que integra farmacogenética, vulnerabilidade neurocomportamental, predisposição metabólica e risco cardiometabólico."
        canonical="/teste-genetico"
        keywords="teste genético, farmacogenética, canabinoides, medicina personalizada, CANNECT Code, Bioma Genetics, GLP-1, metabolismo"
      />

      <section className="relative flex min-h-[calc(100vh-300px)] items-center overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute left-1/4 top-1/4 h-96 w-96 rounded-full bg-white/20 blur-3xl" />
          <div className="absolute bottom-0 right-1/4 h-64 w-64 rounded-full bg-success-400/30 blur-3xl" />
        </div>

        <div className="relative z-10 mx-auto flex w-full max-w-5xl flex-col items-center px-6 py-12 text-center md:py-16">
          <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-medium uppercase tracking-wider text-white/90 backdrop-blur-sm">
            <Dna size={14} />
            Genética aplicada à clínica
          </span>

          <Typography.Heading
            type="headingOne"
            weight="bold"
            level={1}
            className="mx-auto max-w-3xl !text-3xl leading-tight !text-white md:!text-5xl md:leading-tight">
            Nenhum paciente é igual. <span className="text-success-400">E o tratamento também não deveria ser.</span>
          </Typography.Heading>

          <Typography.Text className="mx-auto mt-6 max-w-2xl !text-base leading-relaxed !text-white/80 md:!text-lg">
            O CANNECT Code é um exame genético de suporte à decisão clínica que integra farmacogenética, vulnerabilidade
            neurocomportamental, predisposição metabólica e risco cardiometabólico.
          </Typography.Text>

          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <Button size="xl" onClick={handleCTA} icon={<ArrowRight size={18} />}>
              Solicitar teste genético
            </Button>
            <Button
              size="xl"
              variant="outline_white"
              onClick={() => {
                document.getElementById('comparison')?.scrollIntoView({ behavior: 'smooth' })
              }}
              icon={<ArrowDown size={18} />}
              iconPlacement="left">
              Ver opções de kit
            </Button>
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-white/60">
            <span className="flex items-center gap-1.5">
              <ShieldCheck size={16} /> Coleta domiciliar
            </span>
            <span className="flex items-center gap-1.5">
              <ShieldCheck size={16} /> Resultado a partir de 72h
            </span>
            <span className="flex items-center gap-1.5">
              <ShieldCheck size={16} /> Parceria Bioma Genetics
            </span>
          </div>
        </div>
      </section>

      <div className="container">
        <section className="py-section">
          <div className="flex flex-col gap-4">
            <Typography.Text
              type="captionOne"
              weight="semibold"
              className="uppercase tracking-widest !text-primary-700">
              O exame
            </Typography.Text>
            <Typography.Heading type="headingThree" weight="regular" level={2} className="max-w-2xl leading-snug">
              O que é o CANNECT Code?
            </Typography.Heading>
            <Typography.Text className="max-w-3xl leading-relaxed" muted>
              Um exame genético modular desenvolvido para apoiar decisões clínicas em diferentes contextos terapêuticos
              — da prescrição de canabinoides à estratificação metabólica, cardiometabólica e comportamental. Ele
              integra quatro pilares:
            </Typography.Text>
          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {pillars.map((pillar) => {
              const Icon = pillar.icon
              return (
                <div
                  key={pillar.title}
                  className="group flex flex-col gap-4 rounded-xl border border-gray-100 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-800 transition-colors group-hover:bg-primary-700 group-hover:text-white">
                    <Icon size={24} />
                  </div>
                  <Typography.Text weight="semibold" className="!text-neutral-900">
                    {pillar.title}
                  </Typography.Text>
                  <Typography.Text type="paragraphTwo" muted className="leading-relaxed">
                    {pillar.description}
                  </Typography.Text>
                </div>
              )
            })}
          </div>
        </section>

        <section className="py-section">
          <div className="flex flex-col gap-4">
            <Typography.Text
              type="captionOne"
              weight="semibold"
              className="uppercase tracking-widest !text-primary-700">
              Processo
            </Typography.Text>
            <Typography.Heading type="headingThree" weight="regular" level={2}>
              Como funciona?
            </Typography.Heading>
          </div>

          <div className="mt-10 grid gap-8 md:grid-cols-3">
            {howItWorksSteps.map((step, index) => (
              <div key={step.number} className="relative flex flex-col gap-4">
                {index < howItWorksSteps.length - 1 && (
                  <div className="absolute left-1/2 top-8 hidden h-px w-full bg-gradient-to-r from-primary-300 to-transparent md:block" />
                )}
                <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-100 font-sans text-2xl font-bold text-primary-800">
                  {step.number}
                </span>
                <Typography.Heading type="headingFive" weight="semibold" level={3}>
                  {step.title}
                </Typography.Heading>
                <Typography.Text type="paragraphTwo" muted className="leading-relaxed">
                  {step.description}
                </Typography.Text>
              </div>
            ))}
          </div>
        </section>

        <section className="py-section">
          <div className="flex flex-col gap-8 rounded-2xl bg-secondary-100 p-8 md:flex-row md:items-center md:gap-12 md:p-12">
            <div className="flex flex-1 flex-col gap-4">
              <Typography.Text
                type="captionOne"
                weight="semibold"
                className="uppercase tracking-widest !text-primary-700">
                Tecnologia e parceria
              </Typography.Text>
              <Typography.Heading type="headingFour" weight="regular" level={2} className="leading-snug">
                Realizado em parceria com a <strong>Bioma Genetics</strong>
              </Typography.Heading>
              <Typography.Text muted className="leading-relaxed">
                Laboratório brasileiro especializado em genética e medicina de precisão, pioneiro no estudo da
                microbiota no Brasil. A Bioma se consolida como o maior laboratório independente de apoio em medicina de
                precisão do país, com mais de <strong className="text-neutral-800">80.000 sequenciamentos</strong>{' '}
                realizados e exames 100% internalizados em sua operação.
              </Typography.Text>
              <Typography.Text muted className="leading-relaxed">
                Seu modelo combina tecnologia avançada, laudos orientados à decisão clínica, assessoria científica
                especializada e logística estruturada para atender profissionais de saúde em todo o Brasil.
              </Typography.Text>
            </div>

            <div className="flex flex-shrink-0 items-center justify-center md:w-72">
              <div className="flex h-40 w-full items-center justify-center rounded-xl bg-[#0c15d0] px-8">
                <img src={biomaLogo} alt="Bioma Genetics" className="h-12 w-auto object-contain" />
              </div>
            </div>
          </div>
        </section>

        <section id="comparison" className="scroll-mt-20 py-section">
          <div className="flex flex-col gap-4">
            <Typography.Text
              type="captionOne"
              weight="semibold"
              className="uppercase tracking-widest !text-primary-700">
              Versões
            </Typography.Text>
            <Typography.Heading type="headingThree" weight="regular" level={2}>
              BÁSICO vs COMPLETO
            </Typography.Heading>
          </div>

          {/* Desktop: tabela compacta (w-fit evita faixa vazia à direita) | Mobile: largura total */}
          <div className="mt-10 w-full rounded-xl border border-gray-100 bg-white shadow-sm sm:mx-auto sm:w-fit sm:max-w-full">
            {/* Header — só desktop */}
            <div className="hidden grid-cols-[minmax(0,64ch)_72px_72px] gap-x-2 border-b border-gray-100 bg-gray-50 px-5 py-3 sm:grid sm:items-center">
              <span className="min-w-0 font-sans text-xs font-semibold uppercase tracking-wider text-neutral-500">
                Funcionalidade
              </span>
              <span className="text-center font-sans text-xs font-semibold uppercase tracking-wider text-neutral-500">
                Básico
              </span>
              <span className="text-center font-sans text-xs font-semibold uppercase tracking-wider text-primary-800">
                Completo
              </span>
            </div>

            {comparisonTable.map((mod) => (
              <div key={mod.module}>
                {/* Cabeçalho do módulo */}
                <div className="border-b border-gray-100 bg-primary-50/60 px-4 py-2.5 sm:px-5">
                  <span className="font-sans text-xs font-bold uppercase tracking-wider text-primary-800">
                    {mod.module}
                  </span>
                </div>

                {mod.rows.map((row) => (
                  <div
                    key={row.feature}
                    className="flex items-center justify-between gap-3 border-b border-gray-50 px-4 py-3 transition-colors last:border-0 hover:bg-gray-50/50 sm:grid sm:grid-cols-[minmax(0,64ch)_72px_72px] sm:items-center sm:justify-normal sm:gap-x-2 sm:px-5">
                    {/* Nome da feature */}
                    <span className="min-w-0 flex-1 pr-2 font-sans text-xs leading-snug text-neutral-700 sm:pr-0 sm:text-sm">
                      {row.feature}
                    </span>

                    {/* Básico + Completo — mobile: grupo à direita; desktop: sm:contents coloca as células no grid */}
                    <div className="flex shrink-0 gap-4 sm:contents">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="block font-sans text-[10px] font-medium text-neutral-400 sm:hidden">
                          Básico
                        </span>
                        {row.basic ? (
                          <Check size={16} className="text-success-700" />
                        ) : (
                          <Minus size={16} className="text-neutral-300" />
                        )}
                      </div>

                      <div className="flex flex-col items-center gap-0.5">
                        <span className="block font-sans text-[10px] font-medium text-primary-700 sm:hidden">
                          Completo
                        </span>
                        {row.complete ? (
                          <Check size={16} className="text-success-700" />
                        ) : (
                          <Minus size={16} className="text-neutral-300" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>

        <section className="py-section">
          <div className="flex flex-col gap-4">
            <Typography.Text
              type="captionOne"
              weight="semibold"
              className="uppercase tracking-widest !text-primary-700">
              Público
            </Typography.Text>
            <Typography.Heading type="headingThree" weight="regular" level={2}>
              Para quem serve?
            </Typography.Heading>
          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {audienceCards.map((card) => {
              const Icon = card.icon
              return (
                <div
                  key={card.title}
                  className="flex flex-col gap-4 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-800">
                    <Icon size={20} />
                  </div>
                  <Typography.Text weight="semibold" className="!text-neutral-900">
                    {card.title}
                  </Typography.Text>
                  <ul className="flex flex-col gap-2">
                    {card.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 font-sans text-sm text-neutral-500">
                        <Check size={14} className="mt-0.5 flex-shrink-0 text-success-700" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>

          <div className="mt-8 rounded-xl border border-warning-200 bg-warning-100/50 px-6 py-4">
            <Typography.Text type="paragraphTwo" className="!text-neutral-700">
              <strong>Observação:</strong> O CANNECT Code pode ser solicitado pelo próprio paciente como ferramenta de
              autoconhecimento. Entretanto, a interpretação deve sempre ser contextualizada por profissional habilitado,
              pois o exame não possui finalidade diagnóstica.
            </Typography.Text>
          </div>
        </section>

        <section className="py-section">
          <div className="flex flex-col gap-4">
            <Typography.Text
              type="captionOne"
              weight="semibold"
              className="uppercase tracking-widest !text-primary-700">
              Interpretação
            </Typography.Text>
            <Typography.Heading type="headingThree" weight="regular" level={2}>
              Como interpretar o CANNECT Code?
            </Typography.Heading>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {interpretationBlocks.map((block) => {
              const Icon = block.icon
              return (
                <div key={block.title} className="flex flex-col gap-4 rounded-xl bg-white p-6 shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
                    <Icon size={20} />
                  </div>
                  <Typography.Text weight="semibold" className="!text-neutral-900">
                    {block.title}
                  </Typography.Text>
                  <ul className="flex flex-col gap-2">
                    {block.items.map((item) => (
                      <li key={item} className="flex items-center gap-2 font-sans text-sm text-neutral-500">
                        <div className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-700" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>

          <div className="mt-6 rounded-xl border border-gray-100 bg-white px-6 py-4 shadow-sm">
            <Typography.Text type="paragraphTwo" muted>
              O exame não é diagnóstico e deve ser interpretado no contexto clínico. O CANNECT Code baseia-se em
              evidência farmacogenética consolidada (CYPs), estudos de associação genômica (GWAS) e diretrizes
              internacionais de farmacogenômica.
            </Typography.Text>
          </div>
        </section>

        <section className="py-section">
          <div className="flex flex-col gap-4 text-center">
            <Typography.Text
              type="captionOne"
              weight="semibold"
              className="uppercase tracking-widest !text-primary-700">
              Investimento
            </Typography.Text>
            <Typography.Heading type="headingThree" weight="regular" level={2}>
              Escolha seu kit
            </Typography.Heading>
          </div>

          <div className="mx-auto mt-10 grid max-w-3xl gap-6 md:grid-cols-2">
            {pricingPlans.map((plan) => (
              <div
                key={plan.name}
                className={`relative flex flex-col gap-5 rounded-2xl border p-8 transition-shadow ${
                  plan.featured
                    ? 'border-primary-700 bg-white shadow-lg ring-1 ring-primary-700/20'
                    : 'border-gray-100 bg-white shadow-sm'
                }`}>
                {plan.featured && (
                  <span className="absolute -top-3 right-6 rounded-full bg-primary-700 px-3 py-1 text-xs font-semibold text-white">
                    {plan.tag}
                  </span>
                )}
                {!plan.featured && (
                  <span className="inline-flex w-fit rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-neutral-600">
                    {plan.tag}
                  </span>
                )}

                <Typography.Heading type="headingFive" weight="semibold" level={3}>
                  {plan.name}
                </Typography.Heading>

                <Typography.Text type="paragraphTwo" muted className="leading-relaxed">
                  {plan.description}
                </Typography.Text>

                <div className="flex items-end gap-3">
                  <span className="font-sans text-sm text-neutral-400 line-through">{plan.originalPrice}</span>
                  <span className="rounded bg-success-100 px-2 py-0.5 text-xs font-bold text-success-700">
                    {plan.discountLabel}
                  </span>
                </div>
                <div>
                  <Typography.Heading type="headingThree" weight="bold" level={4} className="!text-primary-800">
                    {plan.discountedPrice}
                  </Typography.Heading>
                  <Typography.Text
                    type="captionOne"
                    muted
                    className="mt-1 block font-sans font-medium !text-primary-700/80">
                    {pricingLaunchLabel}
                  </Typography.Text>
                </div>

                <div className="flex flex-col gap-3 border-t border-gray-100 pt-5">
                  {plan.highlights.map((h) => {
                    const HIcon = h.icon
                    return (
                      <span key={h.text} className="flex items-center gap-2 font-sans text-sm text-neutral-600">
                        <HIcon size={16} className="text-primary-700" /> {h.text}
                      </span>
                    )
                  })}
                </div>

                {plan.footnote && (
                  <Typography.Text type="captionOne" muted className="leading-snug !text-neutral-500">
                    {plan.footnote}
                  </Typography.Text>
                )}

                <Button
                  size="lg"
                  full
                  variant={plan.featured ? 'default' : 'outline'}
                  onClick={handleCTA}
                  icon={<ArrowRight size={16} />}
                  className="mt-auto">
                  Solicitar exame
                </Button>
              </div>
            ))}
          </div>
        </section>

        <section className="py-section">
          <div className="mx-auto max-w-3xl">
            <div className="mb-8 flex flex-col gap-4">
              <Typography.Text
                type="captionOne"
                weight="semibold"
                className="uppercase tracking-widest !text-primary-700">
                Dúvidas
              </Typography.Text>
              <Typography.Heading type="headingThree" weight="regular" level={2}>
                Perguntas frequentes
              </Typography.Heading>
            </div>

            {/* Tabs — PLH tab só aparece para profissionais logados */}
            {isProfessional && (
              <div className="mb-6 flex gap-2">
                <button
                  onClick={() => setActiveFaqTab('patient')}
                  className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${
                    activeFaqTab === 'patient'
                      ? 'border-primary-700 bg-primary-700 text-white'
                      : 'border-gray-200 bg-white text-neutral-600 hover:border-primary-300 hover:text-primary-800'
                  }`}>
                  <User size={16} />
                  Pacientes
                </button>
                <button
                  onClick={() => setActiveFaqTab('professional')}
                  className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${
                    activeFaqTab === 'professional'
                      ? 'border-primary-700 bg-primary-700 text-white'
                      : 'border-gray-200 bg-white text-neutral-600 hover:border-primary-300 hover:text-primary-800'
                  }`}>
                  <Stethoscope size={16} />
                  Profissionais de Saúde
                </button>
              </div>
            )}

            {/* FAQ Pacientes — sempre visível (tab ativa ou se não é PLH) */}
            {(activeFaqTab === 'patient' || !isProfessional) && (
              <Accordion.Root type="single" collapsible className="w-full">
                {faqPatientItems.map((faq, i) => (
                  <Accordion.Item key={`patient-${i}`} value={`patient-faq-${i}`}>
                    <Accordion.Trigger className="text-left !text-base !font-semibold">
                      {faq.question}
                    </Accordion.Trigger>
                    <Accordion.Content>
                      <Typography.Text type="paragraphTwo" muted className="leading-relaxed">
                        {faq.answer}
                      </Typography.Text>
                    </Accordion.Content>
                  </Accordion.Item>
                ))}
              </Accordion.Root>
            )}

            {/* FAQ Profissionais — só aparece quando PLH logado seleciona a tab */}
            {isProfessional && activeFaqTab === 'professional' && (
              <Accordion.Root type="single" collapsible className="w-full">
                {faqProfessionalItems.map((faq, i) => (
                  <Accordion.Item key={`pro-${i}`} value={`pro-faq-${i}`}>
                    <Accordion.Trigger className="text-left !text-base !font-semibold">
                      {faq.question}
                    </Accordion.Trigger>
                    <Accordion.Content>
                      <Typography.Text type="paragraphTwo" muted className="leading-relaxed">
                        {faq.answer}
                      </Typography.Text>
                    </Accordion.Content>
                  </Accordion.Item>
                ))}
              </Accordion.Root>
            )}
          </div>
        </section>

        {/* ── CTA FINAL ──────────────────────────────────────── */}
        <section className="py-section">
          <div className="flex flex-col items-center gap-6 rounded-2xl bg-gradient-to-br from-primary-800 to-primary-700 px-8 py-16 text-center">
            <Dna size={40} className="text-white/70" />
            <Typography.Heading type="headingThree" weight="semibold" level={2} className="max-w-xl !text-white">
              Decisão clínica mais segura começa com genética.
            </Typography.Heading>
            <Button size="xl" variant="outline_white" onClick={handleCTA} icon={<ArrowRight size={18} />}>
              Solicite o CANNECT Code
            </Button>
          </div>
        </section>

        {/* ── RODAPÉ PARCERIA ────────────────────────────────── */}
        <section className="flex flex-col items-center gap-6 pb-16 pt-8">
          <Typography.Text type="paragraphTwo" muted>
            Uma parceria
          </Typography.Text>
          <div className="flex items-center gap-8">
            <img src={cannectLogo} alt="Cannect" className="h-8 w-auto object-contain" />
            <span className="text-2xl text-neutral-300">+</span>
            <div className="flex h-14 items-center justify-center rounded-lg bg-[#0c15d0] px-5">
              <img src={biomaLogo} alt="Bioma Genetics" className="h-6 w-auto object-contain" />
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
